<template>
  <div
    v-click-outside="hideMenu"
    @click="e => showMenu(e)"
    class="card tw:relative tw:min-w-[200px] tw:w-full tw:md:w-[49%]"
    :class="[
      isActionable && !isMobile ? 'tw:hover:cursor-pointer' : 'tw:hover:cursor-default',
      preventHoverEffect ? 'no-hover-effect' : '',
    ]"
  >
    <div
      class="tw:h-full tw:border tw:border-gray-300 tw:p-3 tw:xl:p-4 tw:rounded-xl tw:flex tw:justify-between tw:items-center"
    >
      <div class="tw:h-full tw:flex tw:flex-col tw:items-start">
        <div class="tw:flex tw:flex-grow tw:relative">
          <p class="title tw:font-bold tw:inline tw:text-sm tw:mb-2 tw:items-start tw:gap-x-1" v-html="title" />
          <Tippy v-if="isCustomTooltip && hasHint" class="tw:absolute tw:right-[-22px]" theme="logocustom">
            <InformationOutline class="tw:fill-none tw:stroke-gray-500 tw:h-[18px] tw:w-[18px] tw:ml-1" />
            <template #content>
              <div>
                <p class="tw:mb-2.5" v-html="hint" />
                <img
                  v-if="partnerId > 0"
                  :src="`https://imgcy.trivago.com/e_trim,f_auto,q_auto,h_50/partnerlogos-s3/${partnerId}`"
                  :alt="partnerName"
                  class="tw:bg-white tw:p-1"
                />
              </div>
            </template>
          </Tippy>
          <UiTooltip
            theme="customdarkwithpadding"
            tooltip-position="top"
            :label="hint"
            v-else-if="showTooltip && hasHint"
          >
            <InformationOutline class="tw:fill-none tw:stroke-gray-500 tw:h-[18px] tw:w-[18px] tw:ml-1" />
          </UiTooltip>
        </div>
        <div>
          <slot name="cardData" />
        </div>
      </div>

      <button
        v-if="isActionable && !isMobile"
        ref="dotBtn"
        class="tw:hover:bg-gray-200 tw:rounded-md tw:cursor-pointer"
        @click="trackEvent"
      >
        <img :src="dots" />
      </button>
      <button
        v-if="isActionable && isMobile"
        ref="dotBtn"
        class="tw:hover:bg-gray-200 tw:rounded-md"
        @click="trackEvent"
      >
        <img :src="dots" />
      </button>
    </div>
    <div
      v-show="menuVisible !== null && type === menuVisible"
      @click.stop
      class="tw:absolute tw:w-full tw:bg-white shadow tw:mt-1 tw:rounded-lg tw:z-30 tw:max-h-[300px] tw:overflow-auto"
    >
      <slot name="dropdownMenu" :closeMenu="hideMenu" />
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import { Tippy } from 'vue-tippy'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'

import appAnalytics from '@/utils/tracking'
import { selectedAccommodationId } from '@/layouts/queries'
import { useIsMobile } from '@/composables/useIsMobile'

const props = defineProps({
  title: String,
  hint: String,
  isActionable: Boolean,
  isCustomTooltip: Boolean,
  partnerId: Number,
  partnerName: String,
  type: String,
  hasHint: Boolean,
  preventHoverEffect: Boolean,
})
const dotBtn = ref(null)
const dots = new URL(`../../../assets/img/rate-connect/dropdown-dots.svg`, import.meta.url)
const menuVisible = ref(null)
const { isMobile } = useIsMobile()
const showMenu = e => {
  if (props.isActionable && !isMobile.value) {
    if (menuVisible.value === props.type) {
      menuVisible.value = null
    } else {
      // Prevent loading event when clicking on dot button
      if (!dotBtn.value.contains(e.target)) {
        appAnalytics.track(appAnalytics.events.RC_STATUS_BAR_CARD_CLICKED, {
          accommodationId: selectedAccommodationId.value,
          cardType: props.type,
        })
      }

      menuVisible.value = props.type
    }
  }
}

const hideMenu = () => {
  menuVisible.value = null
}

const showTooltip = computed(() => {
  if (!props.isActionable && props.type === 'campaignType') return false
  else return true
})
const trackEvent = () => {
  if (isMobile.value) {
    if (menuVisible.value === props.type) {
      menuVisible.value = null
    } else {
      menuVisible.value = props.type
    }
  }
  appAnalytics.track(appAnalytics.events.RC_STATUS_BAR_DOTS_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    cardType: props.type,
  })
}
</script>

<style scoped>
.title :deep(span) {
  font-weight: normal;
  font-size: 14px;
}
.card:not(.no-hover-effect):hover button {
  @media (min-width: 768px) {
    background-color: var(--tw-color-gray-200);
  }
}
</style>
